<template>
  <v-app>
    <v-navigation-drawer v-model="drawer" app fixed v-if="logged" :width="350">
      <v-list>
        <template v-for="item in items">
          <v-list-item v-bind="item.content" :key="item.title" v-if="item.type === 'v-list-item'">
            <v-list-item-action>
              <v-icon v-html="item.icon"></v-icon>
            </v-list-item-action>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>

          <template v-if="item.type === 'v-list-group'">
            <v-list-group v-bind="item.content" :key="item.title">
              <v-list-item slot="activator">
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>

              <v-list-item
                v-for="subItem in item.items"
                :key="subItem.title"
                v-bind="subItem.content"
              >
                <v-list-item-title>{{ subItem.title }}</v-list-item-title>
                <v-icon>{{ subItem.icon }}</v-icon>
              </v-list-item>
            </v-list-group>
          </template>
        </template>

        <v-list-item @click="logout">
          <v-list-item-action>
            <v-icon>logout</v-icon>
          </v-list-item-action>
          <v-list-item-title>{{ $t('title.logout') }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar app absolute>
      <v-app-bar-nav-icon @click="drawer = !drawer" v-if="logged"></v-app-bar-nav-icon>
      <v-slide-x-transition mode="out-in">
        <v-toolbar-title :key="$store.state.title" v-text="$store.state.title"></v-toolbar-title>
      </v-slide-x-transition>
      <v-spacer></v-spacer>
      <v-btn text v-if="logged" :to="{ name: 'User' }">
        <v-icon left>account_circle</v-icon>
        {{ $store.state.userData.display_name }}
      </v-btn>
      <v-btn fab depressed small class="locale-button" @click="changeLocale()">{{ toChangeLocale }}</v-btn>
    </v-app-bar>

    <v-dialog transition="false" v-if="loading" v-model="loading" persistent fullscreen>
      <v-container fill-height>
        <v-layout row justify-center align-center>
          <v-progress-circular indeterminate :size="70" :width="7" color="primary"></v-progress-circular>
        </v-layout>
      </v-container>
    </v-dialog>

    <v-main :class="{ overlay: loading }">
      <v-container fluid>
        <v-slide-y-transition mode="out-in">
          <router-view :key="$route.fullPath"/>
        </v-slide-y-transition>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { mapGetters } from 'vuex'
import authService from '@/services/auth'

export default {
  name: 'App',

  data () {
    return {
      drawer: false,
      loading: false
    }
  },

  computed: {
    ...mapGetters({
      logged: 'hasLoggedIn'
    }),

    toChangeLocale () {
      return this.$i18n.locale === 'zh-hk' ? 'Eng' : '中文'
    },

    items () {
      return [
        {
          type: 'v-list-item',
          icon: 'store',
          title: this.$t('title.home'),
          content: {
            to: {
              name: 'Home'
            }
          }
        },
        {
          type: 'v-list-group',
          title: this.$tc('machine', 2),
          content: {
            'prepend-icon': 'tablet_android',
            'no-action': true,
            'value': true
          },
          items: this.machinesItems
        },
        {
          type: 'v-list-group',
          title: this.$tc('location', 2),
          content: {
            'prepend-icon': 'location_on',
            'no-action': true,
            'value': false
          },
          items: this.locationsItems
        },
        {
          type: 'v-list-item',
          icon: 'credit_card',
          title: this.$t('title.manage'),
          content: {
            to: {
              name: 'Manage'
            }
          }
        }
      ]
    },

    locationsItems () {
      let result = []
      for (let key in this.$store.state.locationsData) {
        let locationData = this.$store.state.locationsData[key]
        result.push({
          title: `${key} ${locationData[0]['machineID'] ? '' : `(${this.$t('relocated')})`}`,
          content: {
            to: {
              name: 'Location',
              params: {
                name: key,
                locationData: locationData
              }
            }
          }
        })
      }

      return result
    },

    machinesItems () {
      let result = []
      for (let key in this.$store.state.machinesData) {
        let machineData = this.$store.state.machinesData[key]
        let title = machineData.franchisee_machine_code
        title += ' (' +
          (machineData.franchisee_machine_reports.length ? machineData.franchisee_machine_reports[0].report_name : this.$t('not_yet_allocated')) +
          ')'
        if (machineData.machine_location_selection) {
          title += ' (' + this.$t('location_selection.is_available') + ')'
        }
        result.push({
          title: title,
          content: {
            to: {
              name: 'Machine',
              params: {
                name: machineData.franchisee_machine_code,
                machineData: machineData
              }
            }
          }
        })
      }

      return result
    }
  },

  watch: {
    logged (value) {
      if (value && window.innerWidth > 1100) {
        this.drawer = true
      }
    }
  },

  created () {
    if (window.innerWidth > 1100) {
      this.drawer = true
    }
  },

  methods: {
    changeLocale () {
      this.$i18n.locale = this.$i18n.locale === 'zh-hk' ? 'en' : 'zh-hk'

      // Ensure the locale of heading is also changed
      let path = this.$router.currentRoute.fullPath
      this.$router.replace('/')
      this.$router.replace(path)
    },

    async logout () {
      this.loading = true
      this.drawer = false
      await authService.logout()
      this.$store.commit('LOGOUT')

      this.loading = false
      this.$router.push({
        name: 'Login'
      })
    }
  }
}
</script>

<style lang="stylus" scoped>
.overlay
  opacity: 0.5

.locale-button
  text-transform: none
</style>
